// Active-upload tracking. The uploader POSTs its live progress to
// /admin/upload-sessions (identity is stamped server-side from the token); owners /
// admins read active sessions for the workspace or one gallery to see who is uploading
// what and how far along they are. Mirrors the tenant-scoped hook shape (galleries.ts)
// + the live-poll pattern (platformScanning.ts).

import { useQuery } from "@tanstack/react-query";
import { photohawkApi } from "@/lib/photohawkApi";
import { tenantHostname } from "@/lib/apiDomain";
import type { TenantScope } from "@/lib/api/galleries";

export type UploadPhase =
  | "presigning"
  | "uploading"
  | "scanning"
  | "done"
  | "error"
  | "cancelled";

/** A live upload session as returned by GET /admin/upload-sessions. */
export interface UploadSession {
  sessionId: string;
  engineGuid: string | null;
  galleryName: string | null;
  kind: "image" | "video";
  total: number;
  completed: number;
  failed: number;
  bytesTotal: number;
  bytesSent: number;
  scanTotal: number;
  scanCompleted: number;
  phase: UploadPhase;
  uploaderName: string | null;
  uploaderEmail: string | null;
  uploaderRole: string | null;
  startedAt: number;
  updatedAt: number;
}

/** Progress payload the uploader POSTs. Identity is added server-side. */
export interface UploadSessionReport {
  sessionId: string;
  engineGuid?: string;
  galleryName?: string;
  folderGuid?: string;
  kind?: "image" | "video";
  total?: number;
  completed?: number;
  failed?: number;
  bytesTotal?: number;
  bytesSent?: number;
  scanTotal?: number;
  scanCompleted?: number;
  phase?: string;
  /** Failed files per reason (failReasons.FailKey → count) — never names. */
  failReasons?: Record<string, number>;
  /** "copy" (previews), "original" (after previews), "resize" or "original". */
  pass?: string;
  /** The browser / OS (user agent, cores, memory). */
  client?: { browser: string; os: string; ua: string; cores?: number; memoryGB?: number };
}

/** Best-effort progress report — never throws, so tracking can't affect an upload. */
export const reportUploadSession = (scope: TenantScope, body: UploadSessionReport): Promise<unknown> =>
  photohawkApi
    .post("/admin/upload-sessions", { service: "admin", hostname: tenantHostname(scope), body })
    .catch(() => null);

export const listActiveUploads = async (
  scope: TenantScope,
  engineGuid?: string,
  signal?: AbortSignal,
): Promise<UploadSession[]> => {
  const res = await photohawkApi.get<{ sessions: UploadSession[] }>("/admin/upload-sessions", {
    service: "admin",
    hostname: tenantHostname(scope),
    params: engineGuid ? { engineGuid } : undefined,
    signal,
  });
  return res?.sessions ?? [];
};

/**
 * Live active-uploads poll (owner/admin only — the endpoint is role-gated). Scope to
 * one gallery with `engineGuid`. Polls every 10s so the console stays current.
 */
export const useActiveUploads = (
  scope: TenantScope,
  { engineGuid, enabled = true }: { engineGuid?: string; enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: ["upload-sessions", scope.alias ?? scope.customDomain ?? "current", engineGuid ?? "all"],
    queryFn: ({ signal }) => listActiveUploads(scope, engineGuid, signal),
    enabled: enabled && (!!scope.alias || !!scope.customDomain),
    refetchInterval: 10_000,
    staleTime: 5_000,
    retry: false,
  });
